
import React, { useRef } from 'react';
import { UserSettings, Reminder, Language, Theme } from '../types';
import { t } from '../locales';
import { Ruler, Heart, Upload } from 'lucide-react';

interface SettingsViewProps {
  settings: UserSettings;
  onUpdateSettings: (newSettings: UserSettings) => void;
  onLogout: () => void;
  onClose: () => void;
  onExportData: () => void;
  onImportData: (file: File) => void;
  onOpenDonation: () => void;
  onCleanData?: () => Promise<void>; 
}

export const SettingsView: React.FC<SettingsViewProps> = ({ 
    settings, onUpdateSettings, onLogout, onClose, onExportData, onImportData, onOpenDonation
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLanguageChange = (lang: Language) => {
    onUpdateSettings({ ...settings, language: lang });
  };

  const handleThemeChange = (theme: Theme) => {
    onUpdateSettings({ ...settings, theme: theme });
  };

  const handleWeightChange = (val: string) => {
    onUpdateSettings({ ...settings, weight: parseFloat(val) || 70 });
  };

  const handleHeightChange = (val: string) => {
    onUpdateSettings({ ...settings, height: parseFloat(val) || 175 });
  };

  const handleAgeChange = (val: string) => {
    onUpdateSettings({ ...settings, age: parseFloat(val) || 30 });
  };

  const handleGenderChange = (gender: 'male' | 'female') => {
    onUpdateSettings({ ...settings, gender });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'VitalEat',
          text: 'Check out this cool nutrition voice logger!',
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing', err);
      }
    } else {
      alert('Web Share API not supported on this browser.');
    }
  };

  const handleFeedback = () => {
    window.location.href = 'mailto:dnotilus@gmail.com?subject=VitalEat Feedback';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
          onImportData(e.target.files[0]);
          // Reset value to allow re-importing same file if needed
          e.target.value = '';
      }
  };

  return (
    <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 overflow-y-auto animate-slideIn">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-4 py-2.5 flex items-center justify-between z-10">
        <h2 className="text-base font-bold text-gray-800 dark:text-white">{t(settings.language, 'settings')}</h2>
        <button 
          onClick={onClose}
          className="p-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-4 flex flex-col gap-5 max-w-md mx-auto">
        
        {/* Biometrics Section */}
        <section>
          <h3 className="text-[10px] font-bold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
            Biometrics & Profile
          </h3>
          <div className="flex flex-col gap-2">
              
              <div className="bg-gray-50 dark:bg-gray-800/50 p-2.5 rounded-xl flex items-center justify-between border border-gray-100 dark:border-gray-800">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Gender</span>
                <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-0.5">
                    <button 
                        onClick={() => handleGenderChange('male')}
                        className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all ${settings.gender === 'male' ? 'bg-white dark:bg-gray-600 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-gray-500'}`}
                    >Male</button>
                    <button 
                        onClick={() => handleGenderChange('female')}
                        className={`px-3 py-1 rounded-md text-[10px] font-bold transition-all ${settings.gender === 'female' ? 'bg-white dark:bg-gray-600 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-gray-500'}`}
                    >Female</button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-2 rounded-xl flex flex-col gap-1 border border-gray-100 dark:border-gray-800">
                      <span className="text-[9px] font-bold text-gray-400 uppercase text-center">Age</span>
                      <input 
                        type="number" value={settings.age || 30}
                        onChange={(e) => handleAgeChange(e.target.value)}
                        className="w-full p-1.5 text-center text-sm rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-bold"
                      />
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-2 rounded-xl flex flex-col gap-1 border border-gray-100 dark:border-gray-800">
                      <span className="text-[9px] font-bold text-gray-400 uppercase text-center">Height (cm)</span>
                      <input 
                        type="number" value={settings.height || 175}
                        onChange={(e) => handleHeightChange(e.target.value)}
                        className="w-full p-1.5 text-center text-sm rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-bold"
                      />
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-2 rounded-xl flex flex-col gap-1 border border-gray-100 dark:border-gray-800">
                      <span className="text-[9px] font-bold text-gray-400 uppercase text-center">Weight (kg)</span>
                      <input 
                        type="number" step="0.1" value={settings.weight || 70}
                        onChange={(e) => handleWeightChange(e.target.value)}
                        className="w-full p-1.5 text-center text-sm rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-bold"
                      />
                  </div>
              </div>
          </div>
        </section>

        {/* Preferences Section */}
        <section>
          <h3 className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2">{t(settings.language, 'preferences')}</h3>
          <div className="flex flex-col gap-2">
            <div className="bg-gray-50 dark:bg-gray-800/50 p-2.5 rounded-xl flex items-center justify-between border border-gray-100 dark:border-gray-800">
               <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{t(settings.language, 'language')}</span>
               <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-0.5">
                  <button 
                    onClick={() => handleLanguageChange('en')}
                    className={`px-3 py-1 rounded-md text-[10px] font-bold transition-colors ${settings.language === 'en' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500'}`}
                  >EN</button>
                  <button 
                    onClick={() => handleLanguageChange('fr')}
                    className={`px-3 py-1 rounded-md text-[10px] font-bold transition-colors ${settings.language === 'fr' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500'}`}
                  >FR</button>
               </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/50 p-2.5 rounded-xl flex items-center justify-between border border-gray-100 dark:border-gray-800">
               <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{t(settings.language, 'darkMode')}</span>
               <button 
                 onClick={() => handleThemeChange(settings.theme === 'dark' ? 'light' : 'dark')}
                 className={`w-10 h-5 rounded-full relative transition-colors ${settings.theme === 'dark' ? 'bg-emerald-500' : 'bg-gray-300'}`}
               >
                 <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${settings.theme === 'dark' ? 'translate-x-5' : ''}`}></div>
               </button>
            </div>
          </div>
        </section>

        {/* Community & Feedback Section */}
        <section>
          <h3 className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" /></svg>
            Community & Support
          </h3>
          <div className="flex flex-col gap-2">
              <button
                onClick={onOpenDonation}
                className="w-full bg-rose-50 dark:bg-rose-900/20 p-3 rounded-xl flex items-center justify-between hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors border border-rose-100 dark:border-rose-800"
              >
                 <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-white dark:bg-rose-900/50 rounded-lg text-rose-500 shadow-sm">
                        <Heart size={16} fill="currentColor" />
                    </div>
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-200">{t(settings.language, 'supportUs')}</span>
                 </div>
              </button>

              <button
                onClick={handleShare}
                className="w-full bg-gray-50 dark:bg-gray-800/50 p-3 rounded-xl flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-100 dark:border-gray-800"
              >
                 <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-500">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/></svg>
                    </div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{t(settings.language, 'shareApp')}</span>
                 </div>
              </button>

              <button
                onClick={handleFeedback}
                className="w-full bg-gray-50 dark:bg-gray-800/50 p-3 rounded-xl flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-100 dark:border-gray-800"
              >
                 <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg text-emerald-500">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                    </div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{t(settings.language, 'sendFeedback')}</span>
                 </div>
              </button>
          </div>
        </section>

        {/* Data Management Section */}
        <section>
          <h3 className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2">{t(settings.language, 'dataManagement')}</h3>
          <div className="flex flex-col gap-2">
            <button
                onClick={onExportData}
                className="w-full bg-gray-50 dark:bg-gray-800/50 p-3 rounded-xl flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-100 dark:border-gray-800"
            >
                <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg text-emerald-500">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                    </div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{t(settings.language, 'exportData')}</span>
                </div>
            </button>

            <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-gray-50 dark:bg-gray-800/50 p-3 rounded-xl flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-100 dark:border-gray-800"
            >
                <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-500">
                        <Upload size={16} />
                    </div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{t(settings.language, 'importData')}</span>
                </div>
            </button>
            <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept=".json" 
                onChange={handleFileChange}
            />
          </div>
        </section>

        <section className="pb-8">
          <button 
            onClick={onLogout}
            className="w-full flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-xl hover:bg-red-100 transition-colors"
          >
             <div className="p-1.5 bg-red-100 dark:bg-red-900/30 rounded-lg text-red-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
             </div>
             <span className="text-xs font-bold text-red-600 dark:text-red-400">{t(settings.language, 'logout')}</span>
          </button>
        </section>

        <div className="text-center pb-4">
            <p className="text-[9px] text-gray-400">VitalEat v0.2.5 • Import/Export Update</p>
        </div>

      </div>
    </div>
  );
};
